import React, { useMemo } from "react";
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { fillTimeline, fmtNum, fmtTick, fmtWib } from "./monUtil";

const axis = { tick: { fontSize: 10, fill: "#94A3B8" } };
const xAxis = (win) => <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} scale="time" tickFormatter={(v) => fmtTick(v, win)} minTickGap={40} {...axis} />;
const when = (ts) => fmtWib(new Date(ts).toISOString(), { dateStyle: "short", timeStyle: "short" });

function Tip({ active, payload, rows }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return <div className="rounded-xl border border-line bg-white p-3 text-xs shadow-lg"><p className="font-bold text-slate-900">{when(p.ts)}</p>{rows(p)}</div>;
}

// Sums per-instance system series into one platform timeline (null when no instance reported).
export function useActiveSeries(sys, win, instance) {
  return useMemo(() => {
    if (!sys) return null;
    const ser = (sys.series || []).filter((s) => !instance || s.instance === instance);
    const lines = ser.map((s) => fillTimeline(s.points || [], win, sys.step_min, { ws_user: null, ws_conversation: null, ws_total_max: null }));
    const base = lines[0] || fillTimeline([], win, sys.step_min, {});
    const sum = (i, k) => { const v = lines.map((l) => l[i]?.[k]).filter((x) => x != null); return v.length ? v.reduce((a, b) => a + b, 0) : null; };
    return base.map((p, i) => ({ ts: p.ts, ws_user: sum(i, "ws_user"), ws_conversation: sum(i, "ws_conversation"), ws_total_max: sum(i, "ws_total_max") }));
  }, [sys, win, instance]);
}

export function ActiveChart({ pts, win }) {
  if (!pts) return <div className="h-64 animate-pulse rounded-2xl bg-slate-50" />;
  const rows = (p) => (p.ws_user == null ? <p className="mt-1 text-slate-400">Tidak ada snapshot</p> : <p className="mt-1 text-slate-600">User <b>{fmtNum(p.ws_user)}</b> · percakapan <b>{fmtNum(p.ws_conversation)}</b> · puncak total <b>{fmtNum(p.ws_total_max)}</b></p>);
  return (
    <div className="h-64" data-testid="mon-ws-active-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={pts} margin={{ left: 0, right: 8, top: 4 }}>
          <CartesianGrid stroke="#E2E8F0" vertical={false} />{xAxis(win)}<YAxis width={44} allowDecimals={false} {...axis} />
          <Tooltip content={<Tip rows={rows} />} /><Legend wrapperStyle={{ fontSize: 11 }} />
          <Area dataKey="ws_total_max" name="Puncak total" stroke="none" fill="#7C3AED" fillOpacity={0.08} isAnimationActive={false} connectNulls={false} />
          <Line dataKey="ws_user" name="Kanal user" stroke="#2563EB" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line dataKey="ws_conversation" name="Kanal percakapan" stroke="#06B6D4" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ConnChart({ connect, session, win }) {
  const pts = useMemo(() => {
    if (!connect || !session) return null;
    const c = fillTimeline(connect.points || [], win, connect.step_min, { rpm: 0, count: 0 });
    const s = new Map(fillTimeline(session.points || [], win, session.step_min, { rpm: 0, count: 0 }).map((p) => [p.ts, p]));
    return c.map((p) => ({ ts: p.ts, connect: p.rpm, disconnect: s.get(p.ts)?.rpm || 0, cc: p.count, dc: s.get(p.ts)?.count || 0, p95: p.p95_ms }));
  }, [connect, session, win]);
  if (!pts) return <div className="h-56 animate-pulse rounded-2xl bg-slate-50" />;
  const rows = (p) => <p className="mt-1 text-slate-600">Connect <b>{fmtNum(p.connect)}</b>/mnt ({p.cc}) · disconnect <b>{fmtNum(p.disconnect)}</b>/mnt ({p.dc})</p>;
  return (
    <div className="h-56" data-testid="mon-ws-conn-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={pts} margin={{ left: 0, right: 8, top: 4 }}>
          <CartesianGrid stroke="#E2E8F0" vertical={false} />{xAxis(win)}<YAxis width={44} {...axis} />
          <Tooltip content={<Tip rows={rows} />} /><Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="connect" name="Connect /mnt" fill="#2563EB" isAnimationActive={false} />
          <Bar dataKey="disconnect" name="Disconnect /mnt" fill="#64748B" isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const REASON = { normal: ["Normal (klien menutup)", "#10B981"], idle_timeout: ["Idle timeout (90 dtk)", "#F59E0B"], error: ["Error server", "#F43F5E"], auth_4401: ["Token tidak valid (4401)", "#7C3AED"], forbidden_4403: ["Tanpa akses (4403)", "#A855F7"] };
const reasonMeta = (r) => REASON[r] || (r?.startsWith("client_") ? [`Klien kode ${r.slice(7)}`, "#64748B"] : [r || "Tidak diketahui", "#CBD5E1"]);

export function ReasonDonut({ logs }) {
  const data = useMemo(() => {
    if (!logs) return null;
    const m = {};
    logs.forEach((l) => { const k = l.close_reason || "unknown"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ key: k, label: reasonMeta(k)[0], color: reasonMeta(k)[1], value: v }));
  }, [logs]);
  if (!data) return <div className="h-56 animate-pulse rounded-2xl bg-slate-50" />;
  if (!data.length) return <p className="py-16 text-center text-sm text-slate-400" data-testid="mon-ws-reasons-empty">Belum ada sesi yang ditutup.</p>;
  const total = logs.length;
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row" data-testid="mon-ws-reasons">
      <div className="relative h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart><Pie data={data} dataKey="value" nameKey="label" innerRadius={52} outerRadius={80} paddingAngle={2} isAnimationActive={false}>{data.map((d) => <Cell key={d.key} fill={d.color} />)}</Pie><Tooltip formatter={(v, n) => [`${v} sesi`, n]} /></PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="font-mono text-2xl font-black text-slate-900">{total}</span><span className="text-[10px] text-slate-400">sesi</span></div>
      </div>
      <ul className="w-full space-y-1.5 text-xs">
        {data.map((d) => (
          <li key={d.key} className="flex items-center gap-2" data-testid={`mon-ws-reason-${d.key}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: d.color }} />
            <span className="flex-1 truncate text-slate-700">{d.label}</span>
            <span className="font-mono font-bold text-slate-900">{d.value}</span>
            <span className="w-12 text-right font-mono text-slate-400">{((d.value / total) * 100).toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
